import { WindowOrder } from '@cutr/constants/windows/windows';
import { useTranslation } from 'react-i18next';

import { InfoMessage } from '@/blocks/InfoMessage';
import { Button, RouterButton } from '@/primitives/Button';
import * as Icons from '@/primitives/Icons';
import { dateFormatter } from '@/utils/format';

import { useSubmitOrder } from '../queries/order/crud';
import { usePositionImages } from './renderer/useStageRefsMap';

const Draft = ({ order }: { order: WindowOrder }) => {
  const { t } = useTranslation();
  const submitOrderMutation = useSubmitOrder();
  const exportBase64Images = usePositionImages(order.positions);

  const onSubmit = () => {
    const form = document.querySelector(
      'form[name="order-title-form"]'
    ) as HTMLFormElement;

    if (form) {
      const isValid = form.checkValidity();

      if (!isValid) {
        form.reportValidity();
        return;
      }
    }

    submitOrderMutation.mutate({
      orderId: order.id,
      base64Images: exportBase64Images(),
    });
  };

  return (
    <>
      <Button
        icon={<Icons.Icon icon={<Icons.Send />} />}
        onClick={onSubmit}
        disabled={submitOrderMutation.isPending}
      >
        {t('reseller.cta.submitOrder')}
      </Button>
      {submitOrderMutation.error && (
        <InfoMessage
          variant="error"
          message={t('reseller.quoteSummary.submitError', {
            error: submitOrderMutation.error.message,
          })}
        />
      )}
    </>
  );
};

const QuoteRequested = ({ order }: { order: WindowOrder }) => {
  const { t } = useTranslation();

  return (
    <InfoMessage
      variant="success"
      message={t('reseller.quoteSummary.submitConfirmation', {
        date: dateFormatter(order.quoteRequestedAt),
      })}
    />
  );
};

const QuoteReady = ({ order }: { order: WindowOrder }) => {
  const { t } = useTranslation();
  return (
    <>
      <RouterButton
        to={`/windows/orders/${order.id}/checkout`}
        icon={<Icons.Icon icon={<Icons.Checkmark />} />}
        className="full"
      >
        {t('reseller.cta.acceptQuote')}
      </RouterButton>
    </>
  );
};

const FooterForStatus: Record<
  WindowOrder['status'],
  null | React.ComponentType<{ order: WindowOrder }>
> = {
  draft: Draft,
  quote_requested: QuoteRequested,
  quote_ready: QuoteReady,
  change_requested: null,
  order_placed: null,
  cancelled: null,
};

export const QuoteSummaryResellerFooter = ({
  order,
}: {
  order: WindowOrder;
}) => {
  const Footer = FooterForStatus[order.status];

  return Footer ? <Footer order={order} /> : null;
};
